import { ModalGenerarOC } from "./ComprasUtils";
import CampanaNotificaciones from "./CampanaNotificaciones";
import BotonInstructivo from "./BotonInstructivo";
import BotonMapaProceso from "./BotonMapaProceso";
import { useState, useEffect } from "react";
import { subirDatos, eliminarDato } from './syncSupabase';
import {
  NATURA_CHECKLIST_APARTADOS_SEED, CATEGORIAS_MATERIAL,
  calcularPctChecklist, umbralAprobacionChecklist, evaluarResultadoChecklist,
  estadoInicialSeriePrograma, registrarResultadoAuditoriaMonitoreo,
} from "./motorFaseCalidad";

// ─── CONSTANTES ───────────────────────────────────────────────
const STORAGE_AUD = 'alqos_auditorias';
const STORAGE_NC = 'alqos_ncs';
const STORAGE_AC = 'alqos_acciones_correctivas';
const STORAGE_PROGRAMAS = 'alqos_programas_calidad_cliente';
const STORAGE_CHECKLISTS = 'alqos_checklist_templates';
const STORAGE_FAMILIAS = 'alqos_familias';
const STORAGE_FASES = 'alqos_fase_familia_linea';

const LOG = {
  info: (msg, data) => console.log(`[AUDITORIAS][INFO] ${msg}`, data || ''),
  error: (msg, err) => console.error(`[AUDITORIAS][ERROR] ${msg}`, err || ''),
};

const storage = {
  get: (key) => { try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch (e) { LOG.error(`get ${key}`, e); return []; } },
  set: (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); return true; } catch (e) { LOG.error(`set ${key}`, e); return false; } },
};

const tiposAuditoria = ["Interna", "Externa", "De certificación", "De seguimiento", "De cliente"];
const estadosAuditoria = {
  planificada: { label: "📅 Planificada", color: "#3b82f6" },
  en_curso: { label: "🔵 En curso", color: "#7B5BF2" },
  completada: { label: "✅ Completada", color: "#22c55e" },
  cancelada: { label: "⛔ Cancelada", color: "#e63946" },
};
const tiposHallazgo = {
  nc_mayor: { label: "🔴 NC Mayor", color: "#e63946", escalamiento: "alto", severidad: "critica" },
  nc_menor: { label: "🟡 NC Menor", color: "#f59e0b", escalamiento: "medio", severidad: "mayor" },
  observacion: { label: "🔵 Observación", color: "#3b82f6", escalamiento: "bajo", severidad: "menor" },
  oportunidad: { label: "🟢 Oportunidad de mejora", color: "#22c55e", escalamiento: "bajo", severidad: "menor" },
};
const normasOpciones = ["ISO 9001:2015", "ISO 14001:2015", "ISO 45001", "FSC", "BPM", "FSSC 22000"];

function fechaHoy() { return new Date().toISOString().slice(0, 10); }
function generarCodAuditoria(lista) {
  const anio = new Date().getFullYear();
  return `AUD-${anio}-${String(lista.length + 1).padStart(3, "0")}`;
}

// ─── GENERAR NC DESDE HALLAZGO ────────────────────────────────
function generarNCDesdeHallazgo(hallazgo, auditoria, usuario) {
  try {
    const ncs = storage.get(STORAGE_NC);
    const anio = new Date().getFullYear();
    const nci = `NCI-${anio}-${String(ncs.length + 1).padStart(3, "0")}`;
    const tipoInfo = tiposHallazgo[hallazgo.tipo] || tiposHallazgo.nc_menor;

    const nueva = {
      nci,
      id: Date.now(),
      origen: "Auditoría",
      tipoNC: hallazgo.tipo === "nc_mayor" ? "Hallazgo de auditoría" : hallazgo.tipo === "nc_menor" ? "Hallazgo de auditoría" : "Incumplimiento documental",
      proceso: auditoria.area || "Auditoría",
      descripcion: `Hallazgo de auditoría ${auditoria.codigo}: ${hallazgo.descripcion}${hallazgo.clausula ? ` | Cláusula: ${hallazgo.clausula}` : ""}`,
      severidad: tipoInfo.severidad,
      escalamiento: tipoInfo.escalamiento,
      estado: "abierta",
      accionInmediata: hallazgo.accion || "",
      responsable: hallazgo.responsable || "",
      fechaCompromiso: hallazgo.plazo || "",
      idAuditoria: auditoria.codigo,
      idHallazgo: hallazgo.id,
      clausulaAfectada: hallazgo.clausula || "",
      normaAuditada: auditoria.norma || "",
      fechaHora: new Date().toISOString().slice(0, 16),
      creadoPor: usuario?.nombre || "Sistema",
      rolCreador: usuario?.rol || "",
      generadaAutomaticamente: true,
      _generadaDesde: "auditoria",
      _source: "localStorage",
      _schema: "nc_v1",
    };

    const nuevasNcs = [...ncs, nueva];
    const ok = storage.set(STORAGE_NC, nuevasNcs);
    if (!ok) throw new Error("Error al guardar NC");
    subirDatos(STORAGE_NC);
    LOG.info("NC generada desde hallazgo de auditoría", { nci, auditoria: auditoria.codigo, tipo: hallazgo.tipo });
    return nci;
  } catch (err) {
    LOG.error("Error al generar NC desde hallazgo", err);
    return null;
  }
}


function generarACDesdeHallazgo(hallazgo, auditoria, nci, usuario) {
  try {
    const acs = JSON.parse(localStorage.getItem(STORAGE_AC) || '[]');
    const anio = new Date().getFullYear();
    const idAccion = "AC-" + anio + "-" + String(acs.length + 1).padStart(3, "0");
    const fechaCompromiso = new Date();
    fechaCompromiso.setDate(fechaCompromiso.getDate() + (hallazgo.tipo === "nc_mayor" ? 15 : 30));
    const nueva = {
      id: Date.now(),
      idAccion,
      idNC: nci,
      tipoAccion: "correctiva",
      estado: "abierta",
      resultadoEficacia: "pendiente",
      causaRaiz: hallazgo.descripcion || "",
      descripcionAccion: hallazgo.accion || "",
      alcance: "Originada en auditoría " + auditoria.codigo,
      responsable: hallazgo.responsable || "",
      fechaCompromiso: fechaCompromiso.toISOString().slice(0, 10),
      fecha: new Date().toISOString().slice(0, 10),
      fechaCreacion: new Date().toISOString(),
      creadoPor: usuario?.nombre || "Sistema",
      rolCreador: usuario?.rol || "",
      procesoNC: auditoria.area || "",
      idAuditoria: auditoria.codigo,
      historialEstados: [],
      _source: "localStorage",
      _schema: "ac_v1",
    };
    const nuevas = [...acs, nueva];
    localStorage.setItem(STORAGE_AC, JSON.stringify(nuevas));
    subirDatos(STORAGE_AC);
    return idAccion;
  } catch(e) { return null; }
}

function templateParaPrograma(templates, idPrograma, categoriaMaterial) {
  return templates.find(t => t.idProgramaCliente === idPrograma && t.categoriaMaterial === categoriaMaterial) || null;
}

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────
export default function Auditorias({ onVolver, oscuro, usuario }) {
  const [modalOCAud, setModalOCAud] = useState(null);
  const [auditorias, setAuditorias] = useState([]);
  const [cargando, setCargando] = useState(true);
  useEffect(() => {
    async function cargar() {
      try {
        const { supabase } = await import('./supabase');
        const { data } = await supabase.from('auditorias').select('data');
        if (data && data.length > 0) setAuditorias(data.map(r => r.data));
        else setAuditorias(storage.get(STORAGE_AUD));
      } catch(e) { setAuditorias(storage.get(STORAGE_AUD)); }
      setCargando(false);
    }
    cargar();
  }, []);
  const [vista, setVista] = useState("listado");
  const [form, setForm] = useState({ estado: "planificada", tipo: "Interna", norma: "ISO 9001:2015", fecha: fechaHoy() });
  const [detalle, setDetalle] = useState(null);
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const [formHallazgo, setFormHallazgo] = useState({ tipo: "nc_menor" });
  const [mostrarFormHallazgo, setMostrarFormHallazgo] = useState(false);
  const [ncGenerada, setNcGenerada] = useState(null);

  const [programas, setProgramas] = useState(() => storage.get(STORAGE_PROGRAMAS));
  const [checklistTemplates, setChecklistTemplates] = useState(() => storage.get(STORAGE_CHECKLISTS));
  const [familias] = useState(() => storage.get(STORAGE_FAMILIAS));
  const [fases] = useState(() => storage.get(STORAGE_FASES));
  const [formChecklist, setFormChecklist] = useState({ respuestas: {} });


  if (cargando) return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background: oscuro ? "#09184D" : "#F0EEFF" }}>
      <p style={{ color: oscuro?"white":"#09184D", fontSize:"16px" }}>Cargando auditorías...</p>
    </div>
  );

  const bg = oscuro ? "#09184D" : "#F0EEFF";
  const cardBg = oscuro ? "#0d2160" : "white";
  const texto = oscuro ? "#EDF2FA" : "#2F3E2E";
  const borde = oscuro ? "#1e3a8a" : "#eee";
  const navBg = oscuro ? "#060f30" : "#09184D";
  const subtexto = oscuro ? "#9aabd4" : "#808085";
  const esAdmin = usuario?.rol === "Administrador";

  const inp = { width: "100%", padding: "8px 12px", borderRadius: "8px", border: `1px solid ${borde}`, fontSize: "13px", boxSizing: "border-box", marginTop: "4px", background: cardBg, color: texto, fontFamily: "sans-serif" };
  const lbl = { fontSize: "12px", color: oscuro ? "#9aabd4" : "#555", fontWeight: "600", display: "block", marginTop: "12px" };
  const card = { background: cardBg, borderRadius: "12px", padding: "1.5rem", border: `1px solid ${borde}`, marginBottom: "12px" };

  const seedNaturaChecklist = () => {
    const programaNatura = programas.find(p => p.codigo === "STD-06482");
    if (!programaNatura) { alert('Primero cargá el programa "Natura STD-06482 v6.0" desde Familias y Líneas → ⚙️ Programas.'); return; }
    const yaExisten = checklistTemplates.filter(t => t.idProgramaCliente === programaNatura.id).length;
    if (yaExisten >= CATEGORIAS_MATERIAL.length) { alert("Ya existen los 3 templates de Natura (por categoría de material)."); return; }
    const nuevos = [...checklistTemplates];
    CATEGORIAS_MATERIAL.forEach((cat, i) => {
      if (!templateParaPrograma(nuevos, programaNatura.id, cat)) {
        nuevos.push({ id: Math.floor(Date.now()) + i, idProgramaCliente: programaNatura.id, categoriaMaterial: cat, nombre: `Natura STD-06482 v6.0 — ${cat}`, apartados: JSON.parse(JSON.stringify(NATURA_CHECKLIST_APARTADOS_SEED)) });
      }
    });
    setChecklistTemplates(nuevos);
    storage.set(STORAGE_CHECKLISTS, nuevos);
    subirDatos(STORAGE_CHECKLISTS);
    alert("Templates creados con los 6 apartados de la norma. Los ítems de cada apartado están vacíos — cargalos desde el documento real de Natura (no se inventó contenido de auditoría).");
  };

  const actualizarTemplate = (idTemplate, apartados) => {
    const nuevos = checklistTemplates.map(t => t.id === idTemplate ? { ...t, apartados } : t);
    setChecklistTemplates(nuevos);
    storage.set(STORAGE_CHECKLISTS, nuevos);
    subirDatos(STORAGE_CHECKLISTS);
  };

  const guardar = () => {
    try {
      const actuales = storage.get(STORAGE_AUD);
      let checklist = null;
      if (form.idProgramaCalidadCliente && form.categoriaMaterial) {
        const template = templateParaPrograma(checklistTemplates, form.idProgramaCalidadCliente, form.categoriaMaterial);
        if (template) {
          const respuestas = Object.entries(formChecklist.respuestas).map(([idItem, r]) => ({ idItem, ...r }));
          const pctCumplimiento = calcularPctChecklist(respuestas);
          const programa = programas.find(p => p.id === form.idProgramaCalidadCliente);
          const fase = form.faseEvaluada || "bronce";
          const umbral = umbralAprobacionChecklist(programa, fase, form.tipoEvaluacion || "ascenso");
          const resultado = evaluarResultadoChecklist(pctCumplimiento, umbral);
          checklist = { idTemplate: template.id, categoriaMaterial: form.categoriaMaterial, tipoEvaluacion: form.tipoEvaluacion, faseEvaluada: fase, respuestas, pctCumplimiento, umbralAplicado: umbral, resultado };
        }
      }
      const nueva = {
        ...form,
        id: Date.now(),
        codigo: generarCodAuditoria(actuales),
        hallazgos: [],
        checklist,
        fechaCreacion: new Date().toISOString(),
        creadoPor: usuario?.nombre || "Sistema",
        rolCreador: usuario?.rol || "",
      };
      const nuevas = [...actuales, nueva];
      storage.set(STORAGE_AUD, nuevas);
      subirDatos(STORAGE_AUD);
      setAuditorias(nuevas);

      // Tercera Regla: Serie A/B es un solo estado por PROGRAMA (proveedor-cliente completo), no
      // por família — opera sobre programa.estadoSerie, se persiste en programas_calidad_cliente.
      if (form.tipoEvaluacion === "monitoreo" && form.idFamiliaLinea && form.resultadoMonitoreo) {
        const programa = programas.find(p => p.id === form.idProgramaCalidadCliente);
        const estadoSeriePrevio = programa.estadoSerie || estadoInicialSeriePrograma();
        const nuevoEstadoSerie = registrarResultadoAuditoriaMonitoreo(estadoSeriePrevio, form.resultadoMonitoreo, programa, form.fecha?.slice(0, 7) || fechaHoy().slice(0, 7), form.idFamiliaLinea);
        const nuevosProgramas = programas.map(p => p.id === programa.id ? { ...p, estadoSerie: nuevoEstadoSerie } : p);
        setProgramas(nuevosProgramas);
        storage.set(STORAGE_PROGRAMAS, nuevosProgramas);
        subirDatos(STORAGE_PROGRAMAS);
      }

      setForm({ estado: "planificada", tipo: "Interna", norma: "ISO 9001:2015", fecha: fechaHoy() });
      setFormChecklist({ respuestas: {} });
      setVista("listado");
      LOG.info("Auditoría guardada", { codigo: nueva.codigo });
    } catch (err) { LOG.error("Error al guardar auditoría", err); }
  };

  const eliminar = async (id) => {
    try {
      const nuevas = auditorias.filter(a => a.id !== id);
      storage.set(STORAGE_AUD, nuevas);
      await eliminarDato(STORAGE_AUD, id);
      setAuditorias(nuevas);
      setConfirmEliminar(null); setDetalle(null); setVista("listado");
    } catch (err) { LOG.error("Error eliminar auditoría", err); }
  };

  const cambiarEstado = (id, nuevoEstado) => {
    try {
      const actualizadas = auditorias.map(a => a.id === id ? { ...a, estado: nuevoEstado } : a);
      storage.set(STORAGE_AUD, actualizadas);
      subirDatos(STORAGE_AUD);
      setAuditorias(actualizadas);
      if (detalle?.id === id) setDetalle(prev => ({ ...prev, estado: nuevoEstado }));
    } catch (err) { LOG.error("Error cambiar estado", err); }
  };

  const agregarHallazgo = () => {
    if (!formHallazgo.descripcion?.trim()) return;
    try {
      const hallazgo = {
        ...formHallazgo,
        id: Date.now(),
        fecha: new Date().toISOString(),
        registradoPor: usuario?.nombre,
        ncGenerada: null,
      };
      const actualizadas = auditorias.map(a => a.id === detalle.id ? { ...a, hallazgos: [...(a.hallazgos || []), hallazgo] } : a);
      storage.set(STORAGE_AUD, actualizadas);
      subirDatos(STORAGE_AUD);
      setAuditorias(actualizadas);
      setDetalle(prev => ({ ...prev, hallazgos: [...(prev.hallazgos || []), hallazgo] }));
      setFormHallazgo({ tipo: "nc_menor" });
      setMostrarFormHallazgo(false);
      LOG.info("Hallazgo agregado", { tipo: hallazgo.tipo });
    } catch (err) { LOG.error("Error agregar hallazgo", err); }
  };

  const eliminarHallazgo = (hallazgoId) => {
    try {
      const actualizadas = auditorias.map(a => a.id === detalle.id ? { ...a, hallazgos: a.hallazgos.filter(h => h.id !== hallazgoId) } : a);
      storage.set(STORAGE_AUD, actualizadas);
      subirDatos(STORAGE_AUD);
      setAuditorias(actualizadas);
      setDetalle(prev => ({ ...prev, hallazgos: prev.hallazgos.filter(h => h.id !== hallazgoId) }));
    } catch (err) { LOG.error("Error eliminar hallazgo", err); }
  };

  // ─── GENERAR NC DESDE HALLAZGO ────────────────────────────
  const generarNC = (hallazgo) => {
    const nci = generarNCDesdeHallazgo(hallazgo, detalle, usuario);
    if (nci) {
      const idAC = generarACDesdeHallazgo(hallazgo, detalle, nci, usuario);
      setNcGenerada(nci);
      const actualizadas = auditorias.map(a => a.id === detalle.id ? {
        ...a,
        hallazgos: a.hallazgos.map(h => h.id === hallazgo.id ? { ...h, ncGenerada: nci, acGenerada: idAC } : h)
      } : a);
      storage.set(STORAGE_AUD, actualizadas);
      subirDatos(STORAGE_AUD);
      setAuditorias(actualizadas);
      setDetalle(prev => ({
        ...prev,
        hallazgos: prev.hallazgos.map(h => h.id === hallazgo.id ? { ...h, ncGenerada: nci, acGenerada: idAC } : h)
      }));
      alert("NC " + nci + (idAC ? " y AC " + idAC : "") + " generadas automáticamente.");
    } else {
      alert("Error al generar la NC.");
    }
  };

  const resumen = {
    total: auditorias.length,
    planificadas: auditorias.filter(a => a.estado === "planificada").length,
    completadas: auditorias.filter(a => a.estado === "completada").length,
    hallazgosTotales: auditorias.reduce((acc, a) => acc + (a.hallazgos?.length || 0), 0),
    ncMayores: auditorias.reduce((acc, a) => acc + (a.hallazgos?.filter(h => h.tipo === "nc_mayor").length || 0), 0),
    ncMenores: auditorias.reduce((acc, a) => acc + (a.hallazgos?.filter(h => h.tipo === "nc_menor").length || 0), 0),
    conNC: auditorias.reduce((acc, a) => acc + (a.hallazgos?.filter(h => h.ncGenerada).length || 0), 0),
    pctCompletadas: auditorias.length > 0 ? Math.round((auditorias.filter(a => a.estado === "completada").length / auditorias.length) * 100) : 0,
  };

  const Header = ({ titulo, onBack, extra }) => (
    <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100, marginBottom: "1.5rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <button onClick={onBack} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Volver</button>
        <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>{titulo}</span>
      </div>
      {extra}
    </div>
  );

  if (confirmEliminar) return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: cardBg, borderRadius: "16px", padding: "2rem", maxWidth: "360px", width: "90%", border: `1px solid ${borde}`, textAlign: "center" }}>
        <p style={{ fontSize: "32px", marginBottom: "1rem" }}>🔎</p>
        <p style={{ fontSize: "15px", fontWeight: "600", color: texto, marginBottom: ".5rem" }}>¿Eliminar esta auditoría?</p>
        <p style={{ fontSize: "13px", color: subtexto, marginBottom: "1.5rem" }}>Se eliminarán también todos sus hallazgos.</p>
        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          <button onClick={() => setConfirmEliminar(null)} style={{ padding: "10px 20px", background: "none", border: `1px solid ${borde}`, borderRadius: "10px", cursor: "pointer", color: texto, fontSize: "13px" }}>Cancelar</button>
          <button onClick={() => eliminar(confirmEliminar)} style={{ padding: "10px 20px", background: "#e63946", border: "none", borderRadius: "10px", cursor: "pointer", color: "white", fontSize: "13px", fontWeight: "600" }}>Eliminar</button>
        </div>
      </div>
    </div>
  );

  if (vista === "detalle" && detalle) return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <Header titulo={detalle.codigo}
        onBack={() => { setDetalle(null); setVista("listado"); setNcGenerada(null); }}
        extra={esAdmin && <button onClick={() => setConfirmEliminar(detalle.id)} style={{ background: "rgba(230,57,70,.2)", border: "1px solid rgba(230,57,70,.4)", color: "#e63946", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "12px" }}>🗑 Eliminar</button>}
      />
      <div style={{ padding: "0 2rem 2rem", maxWidth: "700px", margin: "0 auto" }}>

        {ncGenerada && (
          <div style={{ background: "#fff0f0", border: "2px solid #e63946", borderRadius: "12px", padding: "1rem", marginBottom: "12px", textAlign: "center" }}>
            <p style={{ fontSize: "14px", fontWeight: "700", color: "#e63946", margin: "0 0 4px" }}>⚠️ NC generada automáticamente</p>
            <p style={{ fontSize: "13px", color: "#2F3E2E", margin: 0 }}>Código: <strong>{ncGenerada}</strong> — Revisá el módulo de No Conformidades</p>
          </div>
        )}

        <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#F0EEFF", border: `1px solid ${oscuro ? "#7B5BF2" : "#d4c8ff"}` }}>
          <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#7B5BF2", marginBottom: "10px" }}>Identificación</h3>
          {[["Código", detalle.codigo], ["Tipo", detalle.tipo], ["Norma", detalle.norma], ["Auditor", detalle.auditor], ["Área/Proceso", detalle.area], ["Fecha", detalle.fecha], ["Fecha cierre", detalle.fechaCierre], ["Creado por", `${detalle.creadoPor} — ${detalle.rolCreador}`]].map(([k, v]) => v && (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", padding: "4px 0", borderBottom: `0.5px solid ${borde}` }}>
              <span style={{ color: subtexto }}>{k}</span>
              <span style={{ color: texto, fontWeight: "500" }}>{v}</span>
            </div>
          ))}
        </div>

        <div style={card}>
          <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#7B5BF2", marginBottom: "12px" }}>Estado</h3>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {Object.entries(estadosAuditoria).map(([val, { label, color }]) => (
              <button key={val} onClick={() => esAdmin && cambiarEstado(detalle.id, val)}
                style={{ padding: "7px 12px", borderRadius: "10px", fontSize: "12px", cursor: esAdmin ? "pointer" : "default", fontWeight: "600", border: detalle.estado === val ? `2px solid ${color}` : `1px solid ${borde}`, background: detalle.estado === val ? `${color}22` : cardBg, color: detalle.estado === val ? color : subtexto }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {detalle.objetivo && <div style={card}><h3 style={{ fontSize: "14px", fontWeight: "600", color: "#09184D", marginBottom: "8px" }}>Objetivo</h3><p style={{ fontSize: "13px", color: texto, lineHeight: "1.6" }}>{detalle.objetivo}</p></div>}
        {detalle.alcance && <div style={card}><h3 style={{ fontSize: "14px", fontWeight: "600", color: "#09184D", marginBottom: "8px" }}>Alcance</h3><p style={{ fontSize: "13px", color: texto, lineHeight: "1.6" }}>{detalle.alcance}</p></div>}
        {detalle.conclusiones && <div style={card}><h3 style={{ fontSize: "14px", fontWeight: "600", color: "#09184D", marginBottom: "8px" }}>Conclusiones</h3><p style={{ fontSize: "13px", color: texto, lineHeight: "1.6" }}>{detalle.conclusiones}</p></div>}

        {detalle.checklist && (
          <div style={{ ...card, background: detalle.checklist.resultado === "aprobado" ? "#22c55e15" : "#e6394615", border: `1px solid ${detalle.checklist.resultado === "aprobado" ? "#22c55e" : "#e63946"}` }}>
            <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#3b82f6", marginBottom: "8px" }}>Checklist de auditoría técnica</h3>
            <p style={{ fontSize: "12px", color: subtexto, margin: "0 0 4px" }}>Categoría: {detalle.checklist.categoriaMaterial} · Tipo: {detalle.checklist.tipoEvaluacion} · Fase evaluada: {detalle.checklist.faseEvaluada}</p>
            <p style={{ fontSize: "20px", fontWeight: "800", color: detalle.checklist.resultado === "aprobado" ? "#22c55e" : "#e63946", margin: 0 }}>{detalle.checklist.pctCumplimiento?.toFixed(1)}%</p>
            <p style={{ fontSize: "12px", color: subtexto, margin: "2px 0 0" }}>Umbral aplicado: {detalle.checklist.umbralAplicado ?? "sin configurar"}% · Resultado: {detalle.checklist.resultado}</p>
            {detalle.resultadoMonitoreo && <p style={{ fontSize: "12px", color: texto, marginTop: "6px" }}>Resultado de monitoreo (Serie A/B): <strong>{detalle.resultadoMonitoreo.replace(/_/g, " ")}</strong></p>}
          </div>
        )}

        {/* HALLAZGOS CON GENERACIÓN DE NC */}
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#e63946", margin: 0 }}>
              Hallazgos ({detalle.hallazgos?.length || 0})
            </h3>
            {esAdmin && <button onClick={() => setMostrarFormHallazgo(m => !m)} style={{ padding: "5px 12px", background: "#e63946", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "12px", fontWeight: "600" }}>+ Agregar</button>}
          </div>

          {mostrarFormHallazgo && (
            <div style={{ background: oscuro ? "#091840" : "#f7f9fd", borderRadius: "10px", padding: "1rem", marginBottom: "12px", border: `1px solid ${borde}` }}>
              <span style={lbl}>Tipo de hallazgo</span>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "6px" }}>
                {Object.entries(tiposHallazgo).map(([val, { label, color }]) => (
                  <button key={val} onClick={() => setFormHallazgo(f => ({ ...f, tipo: val }))}
                    style={{ padding: "5px 10px", borderRadius: "8px", fontSize: "11px", cursor: "pointer", fontWeight: "600", border: formHallazgo.tipo === val ? `2px solid ${color}` : `1px solid ${borde}`, background: formHallazgo.tipo === val ? `${color}22` : cardBg, color: formHallazgo.tipo === val ? color : subtexto }}>
                    {label}
                  </button>
                ))}
              </div>
              <span style={lbl}>Descripción del hallazgo *</span>
              <textarea value={formHallazgo.descripcion || ""} onChange={e => setFormHallazgo(f => ({ ...f, descripcion: e.target.value }))} rows={3} style={{ ...inp, resize: "none" }} placeholder="Describirlo con referencia al requisito de la norma..." />
              <span style={lbl}>Cláusula de norma afectada</span>
              <input value={formHallazgo.clausula || ""} onChange={e => setFormHallazgo(f => ({ ...f, clausula: e.target.value }))} style={inp} placeholder="Ej: 8.5.1, 9.1.3" />
              <span style={lbl}>Acción requerida</span>
              <input value={formHallazgo.accion || ""} onChange={e => setFormHallazgo(f => ({ ...f, accion: e.target.value }))} style={inp} placeholder="Qué se debe hacer para cerrar este hallazgo" />
              <span style={lbl}>Responsable</span>
              <input value={formHallazgo.responsable || ""} onChange={e => setFormHallazgo(f => ({ ...f, responsable: e.target.value }))} style={inp} />
              <span style={lbl}>Plazo</span>
              <input type="date" value={formHallazgo.plazo || ""} onChange={e => setFormHallazgo(f => ({ ...f, plazo: e.target.value }))} style={inp} />
              <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                <button onClick={agregarHallazgo} style={{ flex: 1, padding: "10px", background: "#7B5BF2", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>Guardar hallazgo</button>
                <button onClick={() => setMostrarFormHallazgo(false)} style={{ padding: "10px 16px", background: "none", border: `1px solid ${borde}`, borderRadius: "8px", cursor: "pointer", color: texto, fontSize: "13px" }}>Cancelar</button>
              </div>
            </div>
          )}

          {(detalle.hallazgos || []).length === 0
            ? <p style={{ fontSize: "13px", color: subtexto, textAlign: "center", padding: "1rem" }}>Sin hallazgos registrados</p>
            : (detalle.hallazgos || []).map(h => (
                <div key={h.id} style={{ background: oscuro ? "#091840" : "#f7f9fd", borderRadius: "10px", padding: "1rem", marginBottom: "8px", border: `1px solid ${h.ncGenerada ? "#e63946" : borde}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "6px" }}>
                    <span style={{ fontSize: "12px", fontWeight: "700", color: tiposHallazgo[h.tipo]?.color }}>{tiposHallazgo[h.tipo]?.label}</span>
                    <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                      {h.ncGenerada && <span style={{ fontSize: "10px", fontWeight: "700", color: "#e63946", background: "#fff0f0", padding: "2px 6px", borderRadius: "6px", border: "1px solid #e63946" }}>NC: {h.ncGenerada}</span>}
                      {esAdmin && !h.ncGenerada && (h.tipo === "nc_mayor" || h.tipo === "nc_menor") && (
                        <button onClick={() => generarNC(h)}
                          style={{ padding: "3px 8px", background: "#e63946", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "10px", fontWeight: "700" }}>
                          🚨 Generar NC
                        </button>
                      )}
                      {esAdmin && <button onClick={() => eliminarHallazgo(h.id)} style={{ background: "none", border: "none", color: "#e63946", cursor: "pointer", fontSize: "16px", padding: "0 4px" }}>×</button>}
                    </div>
                  </div>
                  <p style={{ fontSize: "13px", color: texto, margin: "0 0 6px", lineHeight: "1.5" }}>{h.descripcion}</p>
                  {h.clausula && <p style={{ fontSize: "11px", color: subtexto, margin: "0 0 2px" }}>Cláusula: {h.clausula}</p>}
                  {h.accion && <p style={{ fontSize: "11px", color: subtexto, margin: "0 0 2px" }}>Acción: {h.accion}</p>}
                  {h.responsable && <p style={{ fontSize: "11px", color: subtexto, margin: "0 0 2px" }}>Responsable: {h.responsable} · Plazo: {h.plazo || "—"}</p>}
                  {h.ncGenerada && <p style={{ fontSize: "11px", color: "#e63946", margin: "4px 0 0", fontWeight: "600" }}>✅ NC: {h.ncGenerada}{h.acGenerada ? " · AC: " + h.acGenerada : ""} — Revisá los módulos correspondientes</p>}
                </div>
              ))
          }
        </div>
      {/* GENERAR OC */}
      {esAdmin && (
        <div style={{ background: cardBg, borderRadius:"12px", padding:"1.2rem 1.5rem", border:`1px solid #f59e0b40`, marginBottom:"12px", marginLeft:"2rem", marginRight:"2rem", maxWidth:"700px", margin:"0 auto 12px" }}>
          <h3 style={{ fontSize:"13px", fontWeight:"700", color:"#f59e0b", marginBottom:"8px" }}>🛒 Generar Orden de Compra</h3>
          <p style={{ fontSize:"12px", color: oscuro?"#9aabd4":"#808085", margin:"0 0 10px" }}>¿Esta auditoría requiere contratar un servicio externo?</p>
          <button onClick={()=>setModalOCAud(detalle)}
            style={{ padding:"8px 16px", background:"#f59e0b", color:"white", border:"none", borderRadius:"8px", cursor:"pointer", fontSize:"12px", fontWeight:"700" }}>
            + Generar OC para esta auditoría
          </button>
          {modalOCAud && (
            <ModalGenerarOC oscuro={oscuro} usuario={usuario} origen="Auditorias"
              referencia={modalOCAud.codigo} categoriaDefault="Servicios"
              onClose={()=>setModalOCAud(null)}
              onCreada={(cod)=>{ alert(`✅ OC generada: ${cod} — revisá Compras`); setModalOCAud(null); }}
            />
          )}
        </div>
      )}
      </div>
    </div>
  );

  if (vista === "nueva") return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <Header titulo="Nueva Auditoría" onBack={() => setVista("listado")} />
      <div style={{ padding: "0 2rem 2rem", maxWidth: "700px", margin: "0 auto" }}>
        <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#F0EEFF", border: `1px solid ${oscuro ? "#7B5BF2" : "#d4c8ff"}` }}>
          <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#7B5BF2", marginBottom: "4px" }}>Trazabilidad automática</h3>
          <p style={{ fontSize: "12px", color: subtexto, margin: 0 }}>Creado por: <strong style={{ color: texto }}>{usuario?.nombre}</strong> — {usuario?.rol}</p>
        </div>
        <div style={card}>
          <h3 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px", color: "#09184D" }}>Datos de la auditoría</h3>
          <span style={lbl}>Tipo</span>
          <select onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))} style={inp} defaultValue="Interna">
            {tiposAuditoria.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <span style={lbl}>Norma auditada</span>
          <select onChange={e => setForm(f => ({ ...f, norma: e.target.value }))} style={inp} defaultValue="ISO 9001:2015">
            {normasOpciones.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <span style={lbl}>Estado inicial</span>
          <div style={{ display: "flex", gap: "8px", marginTop: "6px", flexWrap: "wrap" }}>
            {Object.entries(estadosAuditoria).map(([val, { label, color }]) => (
              <button key={val} onClick={() => setForm(f => ({ ...f, estado: val }))}
                style={{ padding: "7px 12px", borderRadius: "10px", fontSize: "12px", cursor: "pointer", fontWeight: "600", border: form.estado === val ? `2px solid ${color}` : `1px solid ${borde}`, background: form.estado === val ? `${color}22` : cardBg, color: form.estado === val ? color : texto }}>
                {label}
              </button>
            ))}
          </div>
          {[{ key: "auditor", label: "Auditor/a responsable" }, { key: "area", label: "Área o proceso auditado" }, { key: "fecha", label: "Fecha de auditoría", type: "date" }, { key: "fechaCierre", label: "Fecha de cierre prevista", type: "date" }, { key: "objetivo", label: "Objetivo de la auditoría", multiline: true }, { key: "alcance", label: "Alcance", multiline: true }, { key: "conclusiones", label: "Conclusiones generales", multiline: true }].map(({ key, label, type, multiline }) => (
            <div key={key}>
              <span style={lbl}>{label}</span>
              {multiline
                ? <textarea rows={3} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} style={{ ...inp, resize: "none" }} />
                : <input type={type || "text"} defaultValue={key === "fecha" ? fechaHoy() : ""} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} style={inp} />
              }
            </div>
          ))}
        </div>

        {/* CHECKLIST DE AUDITORÍA TÉCNICA (configurable por programa de cliente) */}
        <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#f8faff", border: "1px solid #3b82f660" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
            <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#3b82f6", margin: 0 }}>Checklist de auditoría técnica (opcional)</h3>
            {esAdmin && <button onClick={seedNaturaChecklist} style={{ padding: "5px 10px", background: "none", border: "1px solid #3b82f6", borderRadius: "8px", cursor: "pointer", fontSize: "11px", color: "#3b82f6" }}>+ Cargar Natura STD-06482</button>}
          </div>
          <span style={lbl}>Programa de calidad de cliente</span>
          <select value={form.idProgramaCalidadCliente || ""} onChange={e => setForm(f => ({ ...f, idProgramaCalidadCliente: e.target.value ? Number(e.target.value) : null, categoriaMaterial: null }))} style={inp}>
            <option value="">— Sin checklist estructurado (usar hallazgos libres) —</option>
            {programas.map(p => <option key={p.id} value={p.id}>{p.cliente} — {p.codigo} v{p.version}</option>)}
          </select>

          {form.idProgramaCalidadCliente && (<>
            <span style={lbl}>Familia / Línea auditada</span>
            <select value={form.idFamiliaLinea || ""} onChange={e => {
              const idFamiliaLinea = e.target.value ? Number(e.target.value) : null;
              const faseFam = fases.find(fx => fx.idFamiliaLinea === idFamiliaLinea && fx.idProgramaCliente === form.idProgramaCalidadCliente);
              setForm(f => ({ ...f, idFamiliaLinea, faseEvaluada: faseFam?.faseActual || "bronce" }));
            }} style={inp}>
              <option value="">— Seleccionar —</option>
              {familias.filter(fam => fam.idProgramaCalidadCliente === form.idProgramaCalidadCliente).map(fam => <option key={fam.id} value={fam.id}>{fam.codigo} — {fam.nombre}</option>)}
            </select>
            <span style={lbl}>Categoría de material</span>
            <select value={form.categoriaMaterial || ""} onChange={e => setForm(f => ({ ...f, categoriaMaterial: e.target.value }))} style={inp}>
              <option value="">— Seleccionar —</option>
              {CATEGORIAS_MATERIAL.map(c => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
            </select>
            <span style={lbl}>Tipo de evaluación</span>
            <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
              {["ascenso", "mantenimiento", "monitoreo"].map(t => (
                <button key={t} onClick={() => setForm(f => ({ ...f, tipoEvaluacion: t }))}
                  style={{ flex: 1, padding: "7px", borderRadius: "8px", fontSize: "11px", cursor: "pointer", fontWeight: "600", border: form.tipoEvaluacion === t ? "2px solid #3b82f6" : `1px solid ${borde}`, background: form.tipoEvaluacion === t ? "#3b82f622" : cardBg, color: form.tipoEvaluacion === t ? "#3b82f6" : texto }}>{t}</button>
              ))}
            </div>
            <span style={lbl}>Fase evaluada</span>
            <select value={form.faseEvaluada || "bronce"} onChange={e => setForm(f => ({ ...f, faseEvaluada: e.target.value }))} style={inp}>
              {["bronce", "plata", "oro"].map(fa => <option key={fa} value={fa}>{fa}</option>)}
            </select>

            {form.categoriaMaterial && (() => {
              const template = templateParaPrograma(checklistTemplates, form.idProgramaCalidadCliente, form.categoriaMaterial);
              if (!template) return <p style={{ fontSize: "12px", color: "#e63946", marginTop: "10px" }}>No hay checklist cargado para esta categoría. Usá "+ Cargar Natura STD-06482" o cargalo a mano.</p>;
              const respuestas = Object.entries(formChecklist.respuestas).map(([idItem, r]) => ({ idItem, ...r }));
              const pct = calcularPctChecklist(respuestas);
              const programa = programas.find(p => p.id === form.idProgramaCalidadCliente);
              const umbral = umbralAprobacionChecklist(programa, form.faseEvaluada || "bronce", form.tipoEvaluacion || "ascenso");
              const resultado = evaluarResultadoChecklist(pct, umbral);
              return (
                <div style={{ marginTop: "14px" }}>
                  {template.apartados.map((ap, ai) => (
                    <div key={ai} style={{ marginBottom: "12px" }}>
                      <p style={{ fontSize: "12px", fontWeight: "700", color: "#3b82f6", margin: "0 0 6px" }}>{ap.nombre}</p>
                      {ap.items.length === 0 && <p style={{ fontSize: "11px", color: subtexto, fontStyle: "italic" }}>Sin ítems cargados en este apartado.</p>}
                      {ap.items.map(item => (
                            <div key={item.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `0.5px solid ${borde}` }}>
                              <span style={{ fontSize: "12px", color: texto, flex: 1 }}>{item.texto}</span>
                              <div style={{ display: "flex", gap: "4px" }}>
                                {[10, 5, 0].map(p => (
                                  <button key={p} onClick={() => setFormChecklist(fc => ({ ...fc, respuestas: { ...fc.respuestas, [item.id]: { ...fc.respuestas[item.id], puntaje: p } } }))}
                                    style={{ padding: "4px 10px", borderRadius: "6px", fontSize: "11px", fontWeight: "700", cursor: "pointer", border: formChecklist.respuestas[item.id]?.puntaje === p ? "2px solid #3b82f6" : `1px solid ${borde}`, background: formChecklist.respuestas[item.id]?.puntaje === p ? "#3b82f622" : cardBg, color: formChecklist.respuestas[item.id]?.puntaje === p ? "#3b82f6" : subtexto }}>{p}</button>
                                ))}
                              </div>
                            </div>
                      ))}
                      {esAdmin && (
                        <input placeholder="+ Agregar ítem a este apartado y Enter..." onKeyDown={ev => {
                          if (ev.key === "Enter" && ev.target.value.trim()) {
                            const nuevosApartados = template.apartados.map((a, i) => i === ai ? { ...a, items: [...a.items, { id: Date.now(), texto: ev.target.value.trim() }] } : a);
                            actualizarTemplate(template.id, nuevosApartados);
                            ev.target.value = "";
                          }
                        }} style={{ ...inp, fontSize: "11px", padding: "6px 10px", marginTop: "4px" }} />
                      )}
                    </div>
                  ))}
                  {pct !== null && (
                    <div style={{ padding: "12px", borderRadius: "10px", background: resultado === "aprobado" ? "#22c55e15" : "#e6394615", border: `1px solid ${resultado === "aprobado" ? "#22c55e" : "#e63946"}` }}>
                      <p style={{ fontSize: "20px", fontWeight: "800", color: resultado === "aprobado" ? "#22c55e" : "#e63946", margin: 0 }}>{pct.toFixed(1)}%</p>
                      <p style={{ fontSize: "12px", color: subtexto, margin: "2px 0 0" }}>Umbral aplicado: {umbral ?? "sin configurar"}% · Resultado: {resultado}</p>
                    </div>
                  )}
                  {form.tipoEvaluacion === "monitoreo" && (<>
                    <span style={lbl}>Resultado de auditoría de monitoreo (Tercera Regla — Serie A/B)</span>
                    <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
                      {["satisfactorio", "parcialmente_satisfactorio", "no_satisfactorio"].map(r => (
                        <button key={r} onClick={() => setForm(f => ({ ...f, resultadoMonitoreo: r }))}
                          style={{ flex: 1, padding: "7px", borderRadius: "8px", fontSize: "10px", cursor: "pointer", fontWeight: "600", border: form.resultadoMonitoreo === r ? "2px solid #7B5BF2" : `1px solid ${borde}`, background: form.resultadoMonitoreo === r ? "#7B5BF222" : cardBg, color: form.resultadoMonitoreo === r ? "#7B5BF2" : texto }}>{r.replace(/_/g, " ")}</button>
                      ))}
                    </div>
                  </>)}
                </div>
              );
            })()}
          </>)}
        </div>

        <button onClick={guardar} style={{ width: "100%", padding: "14px", background: "#7B5BF2", color: "white", border: "none", borderRadius: "10px", fontSize: "15px", fontWeight: "600", cursor: "pointer", marginTop: "1rem" }}>
          Guardar Auditoría
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button onClick={onVolver} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Menú</button>
          <BotonInstructivo moduloKey="auditorias" oscuro={oscuro} />
          {usuario?.rol === "Administrador" && <BotonMapaProceso moduloKey="auditorias" oscuro={oscuro} />}
          <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>Auditorías</span>
        </div>
        <CampanaNotificaciones oscuro={oscuro} modulo="Auditorias" />
        {esAdmin && <button onClick={() => setVista("nueva")} style={{ padding: "6px 14px", background: "#7B5BF2", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>+ Nueva</button>}
      </div>
      <div style={{ padding: "1rem 1.5rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "10px", marginBottom: "1.5rem" }}>
          {[["Total", resumen.total, "#7B5BF2"], ["Planificadas", resumen.planificadas, "#3b82f6"], ["Completadas", resumen.completadas, "#22c55e"], ["Hallazgos", resumen.hallazgosTotales, "#e63946"], ["NC Mayores", resumen.ncMayores, "#e63946"], ["NC Menores", resumen.ncMenores, "#f59e0b"], ["NC generadas", resumen.conNC, "#7B5BF2"], ["% Completadas", resumen.pctCompletadas + "%", "#22c55e"]].map(([lab, val, col]) => (
            <div key={lab} style={{ background: cardBg, borderRadius: "12px", padding: "1rem", border: `1px solid ${borde}`, textAlign: "center" }}>
              <p style={{ fontSize: "22px", fontWeight: "700", color: col, margin: 0 }}>{val}</p>
              <p style={{ fontSize: "11px", color: subtexto, margin: "4px 0 0" }}>{lab}</p>
            </div>
          ))}
        </div>
        {auditorias.length === 0
          ? <div style={{ textAlign: "center", padding: "4rem" }}><p style={{ fontSize: "40px" }}>🔎</p><p style={{ fontSize: "15px", color: texto }}>No hay auditorías registradas</p><p style={{ fontSize: "13px", color: subtexto }}>Hacé clic en "+ Nueva" para comenzar</p></div>
          : auditorias.map(a => (
              <div key={a.id} onClick={() => { setDetalle(a); setVista("detalle"); setNcGenerada(null); }}
                style={{ background: cardBg, borderRadius: "12px", padding: "1.2rem 1.5rem", border: `1px solid ${borde}`, marginBottom: "10px", cursor: "pointer" }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(123,91,242,0.15)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <span style={{ fontWeight: "700", color: "#7B5BF2", fontSize: "13px" }}>{a.codigo}</span>
                    <span style={{ marginLeft: "10px", fontSize: "13px", color: texto, fontWeight: "600" }}>{a.tipo} — {a.norma}</span>
                    <p style={{ fontSize: "12px", color: subtexto, marginTop: "4px" }}>{a.area || "Área no especificada"} · Auditor: {a.auditor || "—"}</p>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px" }}>
                    <span style={{ fontSize: "11px", fontWeight: "700", color: estadosAuditoria[a.estado]?.color }}>{estadosAuditoria[a.estado]?.label}</span>
                    <span style={{ fontSize: "11px", color: subtexto }}>{a.hallazgos?.length || 0} hallazgos</span>
                    {a.hallazgos?.some(h => h.ncGenerada) && <span style={{ fontSize: "10px", color: "#e63946", fontWeight: "600" }}>⚠️ {a.hallazgos.filter(h => h.ncGenerada).length} NC generada(s)</span>}
                  </div>
                </div>
                <div style={{ marginTop: "6px", fontSize: "11px", color: oscuro ? "#7B5BF2" : "#9b8cc4" }}>Fecha: {a.fecha} · Creado por: {a.creadoPor}</div>
              </div>
            ))
        }
      </div>
    </div>
  );
}